import React, { useEffect, useState, useCallback } from 'react';
import { userAPI, rbacAPI, formatApiErrorDetail, callingAPI, leadControlsAPI } from '../api';
import { toast } from 'sonner';
import {
  Users, Plus, Edit, Trash2, KeyRound, Power, PowerOff,
  Search, X, Shield, PhoneCall, ClipboardList
} from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { WorkPolicyEditor } from '../components/WorkPolicyEditor';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  manager: 'Manager',
  sales_executive: 'Sales Executive',
  site_engineer: 'Site Engineer',
  accountant: 'Accountant', architect: 'Architect', architect_manager: 'Architect Manager'
};

const ROLE_COLORS = {
  super_admin: 'bg-primary/10 text-primary',
  manager: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  sales_executive: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  site_engineer: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  accountant: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  architect: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  architect_manager: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300'
};

export const UserManagementPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetUserId, setResetUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [roles, setRoles] = useState([]);
  const [roleFilter, setRoleFilter] = useState('');
  const [showCallingModal, setShowCallingModal] = useState(false);
  const [callingUser, setCallingUser] = useState(null);
  const [callingControls, setCallingControls] = useState(null);

  const CALLING_CONTROLS = [
    ['calling_app_access', 'Calling App Access', 'Allow this employee\'s Android device to sync calls & enroll'],
    ['my_calls_access', 'My Calls Access', 'Show the My Calls page & own call analytics'],
    ['click_to_call_access', 'Click-to-Call Access', 'Allow sending dial commands from CRM to their phone'],
    ['unmatched_visibility', 'Unmatched Calls Visibility', 'Let employee see & link their unmatched calls']
  ];

  const openCallingControls = async (u) => {
    setCallingUser(u);
    setCallingControls(null);
    setShowCallingModal(true);
    try {
      const { data } = await callingAPI.getControls(u.id);
      setCallingControls(data);
    } catch (e) {
      setCallingControls({ calling_app_access: true, my_calls_access: true, click_to_call_access: true, unmatched_visibility: true });
    }
  };

  const saveCallingControls = async () => {
    try {
      await callingAPI.setControls(callingUser.id, callingControls);
      toast.success('Calling controls updated');
      setShowCallingModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail, 'Failed to update calling controls'));
    }
  };

  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadUser, setLeadUser] = useState(null);
  const [leadControls, setLeadControls] = useState(null);
  const LEAD_CONTROLS = [
    ['self_create', 'Self Lead Creation', 'Allow this user to create leads themselves'],
    ['auto_assign', 'Auto-Assign Self-Created Leads', 'Automatically assign new self-created leads to the creator'],
    ['require_approval', 'Self-Created Leads Require Approval', 'New self-created leads stay Pending until an admin/manager approves'],
    ['can_delete', 'Lead Delete Permission', 'Allow this user to delete their own leads (soft-delete to Recycle Bin)']
  ];
  const openLeadControls = async (u) => {
    setLeadUser(u);
    setLeadControls(null);
    setShowLeadModal(true);
    try {
      const { data } = await leadControlsAPI.get(u.id);
      setLeadControls(data);
    } catch (e) {
      setLeadControls({ self_create: true, auto_assign: true, require_approval: false, can_delete: false });
    }
  };
  const saveLeadControls = async () => {
    try {
      await leadControlsAPI.set(leadUser.id, leadControls);
      toast.success('Lead controls updated');
      setShowLeadModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail, 'Failed to update lead controls'));
    }
  };

  const emptyForm = {
    name: '', email: '', password: '', mobile: '', role: 'sales_executive', is_active: true,
    employee_id: '', department: '', reporting_manager: ''
  };
  const [formData, setFormData] = useState(emptyForm);

  const fetchUsers = useCallback(async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([userAPI.getAll(), rbacAPI.getRoles()]);
      setUsers(usersRes.data); setRoles((rolesRes.data || []).filter(r => r.is_active !== false));
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openCreate = () => {
    setEditingUser(null);
    setFormData(emptyForm);
    setShowFormModal(true);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setFormData({
      name: u.name,
      email: u.email,
      password: '',
      mobile: u.mobile || '',
      role: u.role,
      is_active: u.is_active !== false,
      employee_id: u.employee_id || '',
      department: u.department || '',
      reporting_manager: u.reporting_manager || ''
    });
    setShowFormModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const { password, email, ...updateData } = formData;
        await userAPI.update(editingUser.id, updateData);
        toast.success('User updated successfully');
      } else {
        await userAPI.create(formData);
        toast.success('User created successfully');
      }
      setShowFormModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail, 'Failed to save user'));
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete "${userName}"? This cannot be undone.`)) return;
    try {
      await userAPI.delete(userId);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail, 'Failed to delete user'));
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await userAPI.toggleActive(u.id);
      toast.success(`User ${u.is_active ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail, 'Failed to toggle status'));
    }
  };

  const openResetPassword = (userId) => {
    setResetUserId(userId);
    setNewPassword('');
    setShowResetModal(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await userAPI.resetPassword(resetUserId, newPassword);
      toast.success('Password reset successfully');
      setShowResetModal(false);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail, 'Failed to reset password'));
    }
  };

  const filteredUsers = users.filter(u => {
    if (roleFilter && u.role !== roleFilter) return false;
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return u.name.toLowerCase().includes(s) ||
           u.email.toLowerCase().includes(s) ||
           (u.user_id || '').toLowerCase().includes(s) ||
           (u.employee_id || '').toLowerCase().includes(s) ||
           (u.mobile || '').includes(s);
  });

  if (loading) {
    return <div className="flex items-center justify-center h-96">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="users-container">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-outfit font-semibold">User Management</h1>
          <p className="text-muted-foreground mt-2">Manage users, roles and access</p>
        </div>
        <div className="flex gap-2">
          <Link to="/roles" data-testid="manage-roles-link" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
            <Shield className="h-4 w-4" />
            Manage Roles
          </Link>
          <button data-testid="add-user-button" onClick={openCreate} className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg">
            <Plus className="h-4 w-4" />
            Add User
          </button>
        </div>
      </div>

      <div className="clay-card p-4">
        <div className="flex gap-3"><div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            data-testid="users-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, email, mobile..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900"
          />
        </div><select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700"><option value="">All roles</option>{roles.map(r => <option key={r.role} value={r.role}>{r.label || ROLE_LABELS[r.role] || r.role}</option>)}</select></div>
      </div>

      <div className="clay-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="users-table">
            <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Name</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">User ID</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Employee ID</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Mobile</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Email</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Role</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Created</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Last Login</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {filteredUsers.map(u => (
                <tr key={u.id} data-testid={`user-row-${u.email}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                        {u.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="font-medium">{u.name}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-muted-foreground">{u.user_id || '—'}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{u.employee_id || '—'}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{u.mobile || '—'}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${ROLE_COLORS[u.role] || 'bg-slate-100 text-slate-700'}`}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                      u.is_active !== false
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${u.is_active !== false ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      {u.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {u.created_at ? format(new Date(u.created_at), 'MMM d, yyyy') : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {u.last_login ? format(new Date(u.last_login), 'MMM d, h:mm a') : 'Never'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`edit-user-${u.email}`} onClick={() => openEdit(u)} title="Edit"
                        className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors">
                        <Edit className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                      </button>
                      <button data-testid={`reset-pwd-${u.email}`} onClick={() => openResetPassword(u.id)} title="Reset Password"
                        className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors">
                        <KeyRound className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                      </button>
                      {currentUser?.role === 'super_admin' && (
                        <button data-testid={`calling-controls-${u.email}`} onClick={() => openCallingControls(u)} title="Calling Controls"
                          className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors">
                          <PhoneCall className="h-4 w-4 text-green-600" />
                        </button>
                      )}
                      {currentUser?.role === 'super_admin' && (
                        <button data-testid={`lead-controls-${u.email}`} onClick={() => openLeadControls(u)} title="Lead Controls"
                          className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors">
                          <ClipboardList className="h-4 w-4 text-blue-600" />
                        </button>
                      )}
                      {u.id !== currentUser?.id && (
                        <>
                          <button data-testid={`toggle-${u.email}`} onClick={() => handleToggleActive(u)} title={u.is_active !== false ? 'Deactivate' : 'Activate'}
                            className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors">
                            {u.is_active !== false ? <PowerOff className="h-4 w-4 text-orange-600" /> : <Power className="h-4 w-4 text-green-600" />}
                          </button>
                          <button data-testid={`delete-${u.email}`} onClick={() => handleDelete(u.id, u.name)} title="Delete"
                            className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors">
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No users found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit User Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowFormModal(false)}>
          <div className="bg-white dark:bg-[#131A2B] rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-outfit font-semibold">{editingUser ? 'Edit User' : 'Add User'}</h2>
              <button onClick={() => setShowFormModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Full Name *</label>
                <input required data-testid="user-form-name" value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input type="email" required disabled={!!editingUser} data-testid="user-form-email" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900 disabled:opacity-60" />
              </div>
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium mb-1">Password *</label>
                  <input type="password" required data-testid="user-form-pwd" value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Mobile</label>
                <input data-testid="user-form-mobile" value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
              </div>
              {editingUser && (
                <div>
                  <label className="block text-sm font-medium mb-1">User ID (login)</label>
                  <input disabled data-testid="user-form-userid" value={editingUser.user_id || 'Auto-generated'}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 font-mono opacity-70" />
                  <p className="text-xs text-muted-foreground mt-1">Auto-generated sequential login ID.</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Employee ID</label>
                  <input data-testid="user-form-employeeid" value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    placeholder="Optional, unique"
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Department</label>
                  <input data-testid="user-form-department" value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Reporting Manager</label>
                <select data-testid="user-form-reporting-manager" value={formData.reporting_manager}
                  onChange={(e) => setFormData({ ...formData, reporting_manager: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900">
                  <option value="">None</option>
                  {users.filter(m => (['architect', 'architect_manager'].includes(formData.role)
                    ? ['architect_manager'] : ['manager', 'super_admin']).includes(m.role)).map(m => (
                    <option key={m.id} value={m.user_id || m.id}>{m.name} ({ROLE_LABELS[m.role] || m.role})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Role *</label>
                <select data-testid="user-form-role" value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900">
                  {roles.map(r => <option key={r.role} value={r.role}>{r.label || ROLE_LABELS[r.role] || r.role}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_active" data-testid="user-form-active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
                <label htmlFor="is_active" className="text-sm">Active</label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowFormModal(false)}
                  className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg">Cancel</button>
                <button type="submit" data-testid="user-form-submit" className="px-4 py-2 bg-primary text-white rounded-lg">
                  {editingUser ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lead Controls Modal (Super Admin) */}
      {showLeadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowLeadModal(false)}>
          <div className="bg-white dark:bg-[#131A2B] rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="lead-controls-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-outfit font-semibold flex items-center gap-2"><ClipboardList className="h-5 w-5 text-blue-600" /> Lead Controls</h2>
              <button onClick={() => setShowLeadModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{leadUser?.name} · {leadUser?.user_id || leadUser?.email}</p>
            {!leadControls ? (
              <div className="py-8 text-center text-muted-foreground text-sm">Loading…</div>
            ) : (
              <div className="space-y-3">
                {LEAD_CONTROLS.map(([key, label, desc]) => (
                  <label key={key} data-testid={`lead-toggle-${key}`} className="flex items-start justify-between gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer">
                    <span>
                      <span className="block text-sm font-medium">{label}</span>
                      <span className="block text-xs text-muted-foreground">{desc}</span>
                    </span>
                    <input type="checkbox" className="mt-1 h-4 w-4"
                      checked={leadControls[key] === true}
                      onChange={(e) => setLeadControls({ ...leadControls, [key]: e.target.checked })} />
                  </label>
                ))}
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setShowLeadModal(false)}
                    className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg">Cancel</button>
                  <button type="button" onClick={saveLeadControls} data-testid="lead-controls-save" className="px-4 py-2 bg-primary text-white rounded-lg">Save</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Calling Controls Modal (Super Admin) */}
      {showCallingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCallingModal(false)}>
          <div className="bg-white dark:bg-[#131A2B] rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="calling-controls-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-outfit font-semibold flex items-center gap-2"><PhoneCall className="h-5 w-5 text-green-600" /> Calling Controls</h2>
              <button onClick={() => setShowCallingModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{callingUser?.name} · {callingUser?.user_id || callingUser?.email}</p>
            {!callingControls ? (
              <div className="py-8 text-center text-muted-foreground text-sm">Loading…</div>
            ) : (
              <div className="space-y-3">
                {CALLING_CONTROLS.map(([key, label, desc]) => (
                  <label key={key} data-testid={`calling-toggle-${key}`} className="flex items-start justify-between gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer">
                    <span>
                      <span className="block text-sm font-medium">{label}</span>
                      <span className="block text-xs text-muted-foreground">{desc}</span>
                    </span>
                    <input type="checkbox" className="mt-1 h-4 w-4"
                      checked={callingControls[key] !== false}
                      onChange={(e) => setCallingControls({ ...callingControls, [key]: e.target.checked })} />
                  </label>
                ))}
                {callingControls.calling_app_access === false && (
                  <p className="text-xs text-amber-600">Turning off Calling App Access will revoke this employee's active device tokens.</p>
                )}
                <div className="border-t border-slate-200 pt-3 dark:border-slate-700">
                  <WorkPolicyEditor employeeId={callingUser.id} employeeName={callingUser?.name}
                    canEdit={currentUser?.role === 'super_admin'} />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setShowCallingModal(false)}
                    className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg">Cancel</button>
                  <button type="button" onClick={saveCallingControls} data-testid="calling-controls-save" className="px-4 py-2 bg-primary text-white rounded-lg">Save</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowResetModal(false)}>
          <div className="bg-white dark:bg-[#131A2B] rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-outfit font-semibold">Reset Password</h2>
              <button onClick={() => setShowResetModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">New Password *</label>
                <input type="password" required minLength={6} data-testid="reset-pwd-input" value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowResetModal(false)}
                  className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg">Cancel</button>
                <button type="submit" data-testid="reset-pwd-submit" className="px-4 py-2 bg-primary text-white rounded-lg">Reset</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
